/**
 * Shared Your Tour header and footer drawing for all PDF letters.
 */

const WHITE = [255, 255, 255];
const BLACK = [17, 24, 39];
const AMBER = [232, 160, 32];

function setColor(doc, rgb) { doc.setTextColor(rgb[0], rgb[1], rgb[2]); }
function setFill(doc, rgb)  { doc.setFillColor(rgb[0], rgb[1], rgb[2]); }
function setDraw(doc, rgb)  { doc.setDrawColor(rgb[0], rgb[1], rgb[2]); }

/**
 * Draws the Your Tour branded header.
 * @param {object} doc         jsPDF instance
 * @param {string|null} logoDataUrl  Your Tour logo as data URL (JPEG)
 * @param {string|null} crestDataUrl Club crest as data URL (optional)
 * @param {string|null} clubName     Club/school name (optional)
 * @returns {number} y where body content should start
 */
export function drawYourTourHeader(doc, logoDataUrl = null, crestDataUrl = null, clubName = null) {
  const pageW   = 210;
  const marginL = 18;
  const marginR = 18;
  const headerH = 46;

  // White base
  setFill(doc, WHITE);
  doc.rect(0, 0, pageW, headerH, 'F');

  // Black chevron polygon (left panel)
  setFill(doc, [0, 0, 0]);
  doc.lines(
    [[112, 0], [14, headerH / 2], [-14, headerH / 2], [-112, 0]],
    0, 0, [1, 1], 'F', true
  );

  // ── Left panel (black) — Your Tour logo + company details ─────────────────
  const leftLogoW = 20;
  const leftLogoH = 20;
  const leftLogoX = marginL;
  const leftLogoY = (headerH - leftLogoH) / 2;
  const leftInfoX = leftLogoX + leftLogoW + 6;
  const leftInfoMaxW = 112 - (leftInfoX - marginL) - 4; // stay clear of the chevron tip

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 'JPEG', leftLogoX, leftLogoY, leftLogoW, leftLogoH);
    } catch (_) {
      drawLogoFallback(doc, leftLogoX, leftLogoY, leftLogoW, leftLogoH);
    }
  } else {
    drawLogoFallback(doc, leftLogoX, leftLogoY, leftLogoW, leftLogoH);
  }

  let ly = 9;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  setColor(doc, WHITE);
  const compLines = doc.splitTextToSize('YOUR TOUR LTD, LLS HQ', leftInfoMaxW);
  doc.text(compLines, leftInfoX, ly);
  ly += compLines.length * 5 + 1.5;

  setDraw(doc, WHITE);
  doc.setLineWidth(0.4);
  doc.line(leftInfoX, ly, marginL + 112 - 4, ly);
  ly += 4.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  setColor(doc, [190, 190, 190]);

  const addrLines = doc.splitTextToSize(
    'Woolton High, Woolton Hill Road, Liverpool, L25 6JA', leftInfoMaxW
  );
  doc.text(addrLines, leftInfoX, ly);
  ly += addrLines.length * 3.8 + 2;

  const regLines = doc.splitTextToSize(
    'Registered in England - Company Number 10432783', leftInfoMaxW
  );
  doc.text(regLines, leftInfoX, ly);

  // ── Right panel (white) — club crest + club name ───────────────────────────
  const rightX = 128; // start of white panel (after chevron tip)
  const crestSize = 20;
  const crestX = pageW - marginR - crestSize;
  const crestY = (headerH - crestSize) / 2;
  const nameMaxW = crestX - rightX - 4;

  if (crestDataUrl) {
    try {
      doc.addImage(crestDataUrl, 'JPEG', crestX, crestY, crestSize, crestSize);
    } catch (_) {
      // ignore crest errors — not critical
    }
  }

  if (clubName) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    setColor(doc, BLACK);
    const nameLines = doc.splitTextToSize(clubName, nameMaxW);
    const nameY = (headerH / 2) - ((nameLines.length - 1) * 2.5) + 1.5;
    doc.text(nameLines, rightX, nameY);
  }

  return headerH + 10; // y where body content should start
}

/**
 * Draws the Your Tour branded footer.
 * @param {object} doc         jsPDF instance
 * @param {number} contentY    Current y after all body content
 * @param {string|null} logoDataUrl  Your Tour logo as data URL (JPEG)
 */
export function drawYourTourFooter(doc, contentY, logoDataUrl) {
  const pageW   = 210;
  const pageH   = 297;
  const marginR = 18;
  const footerH = 38;
  const footerY = Math.max(contentY + 10, pageH - footerH);

  // Black background
  setFill(doc, [0, 0, 0]);
  doc.rect(0, footerY, pageW, pageH - footerY, 'F');

  // Decorative white diagonal lines — kept in the far-left zone (< 28 mm)
  // so they never reach the text columns (which start at ~30 mm)
  setDraw(doc, WHITE);
  doc.setLineWidth(1.8);
  doc.line(0,  pageH, 14, footerY + 1);
  doc.line(12, pageH, 26, footerY + 1);

  // Logo — far right, well clear of text columns
  const logoW = 26;
  const logoH = 26;
  const logoX = pageW - marginR - logoW + 4;  // 170 mm
  const logoYPos = footerY + (footerH - logoH) / 2;

  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, 'JPEG', logoX, logoYPos, logoW, logoH);
    } catch (_) {
      drawLogoFallback(doc, logoX, logoYPos, logoW, logoH);
    }
  } else {
    drawLogoFallback(doc, logoX, logoYPos, logoW, logoH);
  }

  // Three text columns — centres at 50 / 95 / 143 mm (well clear of lines & logo)
  const c1 = 50;
  const c2 = 95;
  const c3 = 143;

  // Row 1 — contact details
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  setColor(doc, WHITE);
  const row1y = footerY + 11;
  doc.text('+44 (151) 294 3229',   c1,  row1y, { align: 'center' });
  doc.text('yourtour@llsonline.uk', c2,  row1y, { align: 'center' });
  doc.text('www.your-tour.co.uk',  c3,  row1y, { align: 'center' });

  // Row 2 — platform names
  doc.setFontSize(6.5);
  setColor(doc, [160, 160, 160]);
  const row2y = footerY + 22;
  doc.text('Instagram', c1, row2y, { align: 'center' });
  doc.text('Facebook',  c2, row2y, { align: 'center' });
  doc.text('Twitter',   c3, row2y, { align: 'center' });

  // Row 3 — handles
  doc.setFontSize(7);
  setColor(doc, WHITE);
  const row3y = footerY + 28;
  doc.text('@llsyourtour', c1, row3y, { align: 'center' });
  doc.text('@llsyourtour', c2, row3y, { align: 'center' });
  doc.text('@llsyourtour', c3, row3y, { align: 'center' });
}

function drawLogoFallback(doc, x, y, w, h) {
  setFill(doc, AMBER);
  doc.rect(x, y, w, h, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  setColor(doc, [0, 0, 0]);
  doc.text('YOUR', x + w / 2, y + h * 0.38, { align: 'center' });
  doc.text('TOUR',  x + w / 2, y + h * 0.65, { align: 'center' });
}
